import React, { FC, useEffect, useState } from 'react';

import {
  Flex,
  Modal,
  Button,
  Center,
  Spinner,
  ModalBody,
  ModalHeader,
  ModalContent,
  ModalOverlay,
  ModalCloseButton,
  InputLeftElement,
} from '@chakra-ui/react';
import { MdCalendarToday } from 'react-icons/md';

import 'react-datepicker/dist/react-datepicker.css';

import { Form, Formik, useFormikContext } from 'formik';

import { useMutation } from 'react-query';
import { initiateHarvestSummary } from 'api/inventory';

import { convertUTCToLocal } from 'utils/dateUtils';

import FormikInput from 'components/primitives/FormikInput';
import FormikDatepicker from 'components/primitives/FormikDatepicker';
import FormikSelectInput from 'components/primitives/FormikSelectInput';

import { InventoryReportTypeMap } from 'constants/itemtypes';

import {
  getInitialValues,
  validationSchema,
} from './EditHarvestSummaryFormModal.utils';

import { EditHarvestSummaryFormModalViewProps } from './EditHarvestSummaryFormModal.props';

import { InventoryCount, InventoryReportTypeEnum } from 'types/inventory';
import { SiteFilterableSelect } from '../SiteFilterableSelect';
import FormikMonthYearPicker from 'components/primitives/FormikMonthYearPicker';

const labelProps = {
  fontSize: '12px',
  color: '#afafaf',
  noOfLines: 1,
};

const DateFields: FC<{ selectedInventoryItem: any }> = (props) => {
  const selectedInventoryItem = props?.selectedInventoryItem;
  const { values, setFieldValue } = useFormikContext<any>();
  const [filteredData, setFilteredData] = useState<InventoryCount[]>([]);
  const site = values?.site as string;
  const reportType =
    values?.reportType === ''
      ? null
      : (Number(values?.reportType) as InventoryReportTypeEnum);
  const mtnFn = async () => {
    if (typeof reportType === 'number' && !!site) {
      return initiateHarvestSummary(site, reportType);
    }
  };

  const { data, mutate, isLoading } = useMutation(mtnFn, {
    onSuccess: (response) => {
      if (response) {
        let filtered = response.list.filter((r) => !r.isArchived);
        setFilteredData(filtered);
        if (
          reportType === InventoryReportTypeEnum.BEGINNING &&
          response?.beginning &&
          !values.startDate
        ) {
          setFieldValue(
            'startDate',
            convertUTCToLocal(response.beginning, true, true),
          );
        }

        if (
          reportType === InventoryReportTypeEnum.ENDING &&
          response?.ending &&
          !values.endDate
        ) {
          setFieldValue(
            'endDate',
            convertUTCToLocal(response.ending, true, true),
          );
        }
      }
    },
  });
  useEffect(() => {
    mutate();
    if (!values.startDate) {
      setFieldValue('startDate', '');
    }
    if (!values.endDate) {
      setFieldValue('endDate', '');
    }
    // Don't clear inventory count values - let them be set by the pre-selection logic
  }, [reportType, site]);

  // Pre-select inventory count values when data is loaded
  useEffect(() => {
    if (filteredData.length > 0 && values.startDate && values.endDate) {
      // Find the inventory count items that match the dates
      const beginningItem = filteredData.find(
        (item) =>
          convertUTCToLocal(item.countDate, true, true) === values.startDate,
      );
      const endingItem = filteredData.find(
        (item) =>
          convertUTCToLocal(item.countDate, true, true) === values.endDate,
      );

      if (beginningItem) {
        setFieldValue('beginningInventoryCount', beginningItem._id);
      }
      if (endingItem) {
        setFieldValue('endingInventoryCount', endingItem._id);
      }
    }
  }, [filteredData, values.startDate, values.endDate, setFieldValue]);

  const handleInventoryCountChange = (selectedValue: any, field: string) => {
    const findInventory = filteredData?.find(
      (item) => item._id === selectedValue,
    );
    if (findInventory) {
      setFieldValue(field, findInventory.countDate);
    }
  };

  const renderInputs = () => {
    switch (reportType) {
      case InventoryReportTypeEnum.BEGINNING: {
        const options = filteredData.map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];

        return (
          <Flex gap="20px" align="flex-start">
            <FormikInput
              name="startDate"
              label="Beginning Inventory Count"
              isRequired
              isReadOnly
              labelProps={labelProps}
              leftElement={
                <InputLeftElement>
                  <MdCalendarToday color="primary.400" />
                </InputLeftElement>
              }
            />

            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Ending Inventory Count"
              name="endingInventoryCount"
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'endDate')
              }>
              {options.map((countDate, index) => (
                <option key={countDate} value={filteredData?.[index]._id}>
                  {countDate}
                </option>
              ))}
            </FormikSelectInput>
          </Flex>
        );
      }
      case InventoryReportTypeEnum.ENDING: {
        const options = (filteredData || []).map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];

        return (
          <Flex gap="20px" align="flex-start">
            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Beginning Inventory Count"
              name="beginningInventoryCount"
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'startDate')
              }>
              {options.map((countDate, index) => (
                <option key={countDate} value={filteredData?.[index]._id}>
                  {countDate}
                </option>
              ))}
            </FormikSelectInput>

            <FormikInput
              name="endDate"
              label="Ending Inventory Count"
              isRequired
              isReadOnly
              labelProps={labelProps}
              leftElement={
                <InputLeftElement>
                  <MdCalendarToday color="primary.400" />
                </InputLeftElement>
              }
            />
          </Flex>
        );
      }
      case InventoryReportTypeEnum.MONTHLY: {
        const beginningOptions = (filteredData || []).map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];

        const endingOptions = (filteredData || []).map((i) =>
          convertUTCToLocal(i.countDate, true, true),
        ) as string[];
        return (
          <Flex gap="20px" align="flex-start">
            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Beginning Inventory Count"
              name="beginningInventoryCount"
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'startDate')
              }>
              {beginningOptions.map((beginningDate, index) => (
                <option key={beginningDate} value={filteredData?.[index]._id}>
                  {beginningDate}
                </option>
              ))}
            </FormikSelectInput>

            <FormikSelectInput
              isRequired
              placeholder="Select"
              label="Ending Inventory Count"
              name="endingInventoryCount"
              selectedInventoryItem={selectedInventoryItem ?? null}
              leftElement={<MdCalendarToday color="primary.400" size="14px" />}
              onChange={(selectedValue: any) =>
                handleInventoryCountChange(selectedValue, 'endDate')
              }>
              {endingOptions.map((countDate, index) => (
                <option key={countDate} value={filteredData?.[index]._id}>
                  {countDate}
                </option>
              ))}
            </FormikSelectInput>
          </Flex>
        );
      }
      case InventoryReportTypeEnum.NO_INVENTORY: {
        return (
          <Flex gap="20px" align="flex-start">
            <FormikDatepicker
              isRequired
              name="startDate"
              label="Beginning Inventory Count"
              labelProps={labelProps}
            />
            <FormikDatepicker
              isRequired
              name="endDate"
              label="Ending Inventory Count"
              labelProps={labelProps}
            />
          </Flex>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {isLoading ? (
        <Center flex={1} py="20px">
          <Spinner />
        </Center>
      ) : (
        renderInputs()
      )}
    </>
  );
};

const EditHarvestSummaryFormModalView: FC<EditHarvestSummaryFormModalViewProps> = (
  props,
) => {
  const { isOpen, onClose, onSubmit, isLoading, report } = props;

  // Debug: Log initial values when report changes
  React.useEffect(() => {
    if (report) {
      const initialVals = getInitialValues(report);
      console.log('Report data:', report);
      console.log('Initial values:', initialVals);
    }
  }, [report]);

  return (
    <Modal
      size="3xl"
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent p="10px 30px" borderRadius="15px">
        <ModalCloseButton _focus={{ outline: 'none' }} top="5" />
        <ModalHeader textAlign="center" fontWeight="600" color="#262626">
          Edit Harvest Summary
        </ModalHeader>
        <ModalBody>
          <Formik
            onSubmit={(values) => {
              console.log('Form values before processing:', values);

              values.startDate = convertUTCToLocal(
                values.startDate,
                true,
                true,
                'YYYY-MM-DD',
              ) as string;
              values.endDate = convertUTCToLocal(
                values.endDate,
                true,
                true,
                'YYYY-MM-DD',
              ) as string;

              const monthYear = values.monthYear.split('/');
              const month = Number(monthYear[0]);
              const year = Number(monthYear[1]);

              const payload = {
                id: report?._id || '',
                site: values.site,
                name: values.name,
                month: month,
                year: year,
                reportType: Number(values.reportType),
                endingInventoryCount: values.endingInventoryCount,
                beginningInventoryCount: values.beginningInventoryCount,
                startDate: values.startDate,
                endDate: values.endDate,
              };

              console.log('Final payload:', payload);
              onSubmit(payload);
            }}
            initialValues={getInitialValues(report)}
            validationSchema={validationSchema}
            enableReinitialize={true}>
            {({ isValid, setFieldValue }) => {
              return (
                <Form>
                  <Flex direction="column" gap="20px">
                    <Flex gap="20px">
                      <SiteFilterableSelect
                        label="Select Customer Site"
                        name="site"
                        isReadOnly={true}
                        selectedInventoryItem={null}
                        labelProps={{ fontSize: '14px' }}
                        isRequired={true}
                      />

                      <FormikSelectInput
                        isRequired
                        isDisabled={true}
                        placeholder="Select"
                        label="Report Type"
                        name="reportType"
                        isFromInventoryCount={false}>
                        {Object.entries(InventoryReportTypeMap)?.map(
                          ([key, value]) => (
                            <option key={value} value={key}>
                              {value || ''}
                            </option>
                          ),
                        )}
                      </FormikSelectInput>
                    </Flex>

                    <DateFields selectedInventoryItem={null} />

                    <Flex gap="20px">
                      <FormikInput
                        name="name"
                        label="Report Name"
                        isRequired
                        labelProps={labelProps}
                      />

                      <FormikMonthYearPicker
                        labelProps={labelProps}
                        onChange={(month, year) => {
                          setFieldValue('monthYear', `${month}/${year}`);
                        }}
                        name="monthYear"
                      />
                    </Flex>

                    <Flex my={8} flexDirection="row" justifyContent="center">
                      <Button
                        variant="outline"
                        marginRight="10px"
                        w="134px"
                        onClick={onClose}
                        isDisabled={isLoading}>
                        Cancel
                      </Button>
                      <Button
                        isDisabled={!isValid}
                        w="134px"
                        type="submit"
                        isLoading={isLoading}>
                        Update
                      </Button>
                    </Flex>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default EditHarvestSummaryFormModalView;
